import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
// @ts-ignore - API is a JavaScript file without type definitions
import API from '../../../context/API.js';
import EarningsAPI from '../../../APIs/earningsAPI';
import moment from 'moment';

// Types
export interface MerchantData {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  devices?: any[];
  currentSessions?: any[];
  [key: string]: any;
}

export interface OrderStatuses {
  completed: number;
  pending: number;
  ready: number;
}

export interface EarningsData {
  summary?: {
    netEarnings?: number;
    totalRevenue?: number;
    totalFees?: number;
  };
  [key: string]: any;
}

export interface DashboardState {
  merchantData: MerchantData | null;
  sales: number;
  orders: number;
  orderStatuses: OrderStatuses;
  earningsData: EarningsData | null;
  storeId: string | null;
  lastUpdated: string;
  loading: {
    merchant: boolean;
    sales: boolean;
    orders: boolean;
    earnings: boolean;
  };
  error: string | null;
}

const initialState: DashboardState = {
  merchantData: null,
  sales: 0,
  orders: 0,
  orderStatuses: {
    completed: 0,
    pending: 0,
    ready: 0,
  },
  earningsData: null,
  storeId: null,
  lastUpdated: 'Never',
  loading: {
    merchant: true,
    sales: false,
    orders: false,
    earnings: false,
  },
  error: null,
};

// Helper function to format time ago
const formatTimeAgo = (date: Date): string => {
  const now = new Date();
  const seconds = Math.round((now.getTime() - date.getTime()) / 1000);
  const minutes = Math.round(seconds / 60);
  const hours = Math.round(minutes / 60);
  const days = Math.round(hours / 24);

  if (seconds < 60) {
    return `${seconds} seconds ago`;
  } else if (minutes < 60) {
    return `${minutes} minutes ago`;
  } else if (hours < 24) {
    return `${hours} hours ago`;
  } else {
    return `${days} days ago`;
  }
};

// Async thunks
export const fetchMerchantData = createAsyncThunk(
  'merchantDashboard/fetchMerchantData',
  async (_, { rejectWithValue }) => {
    const role = localStorage.getItem('role');
    if (role !== 'merchant') {
      return rejectWithValue('Merchant profile is only for merchant role');
    }
    try {
      const token = localStorage.getItem('access_token');
      const response = await API.get('merchant/profile', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const now = Date.now();
      localStorage.setItem('lastUpdatedTimestamp', now.toString());
      return {
        data: response.data.data,
        lastUpdated: formatTimeAgo(new Date(now)),
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch merchant data');
    }
  }
);

export const fetchStores = createAsyncThunk(
  'merchantDashboard/fetchStores',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await API.get('/stores', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const stores = response.data.data || [];
      return stores.length > 0 ? stores[0].id : null;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch stores');
    }
  }
);

export const fetchOrderStatuses = createAsyncThunk(
  'merchantDashboard/fetchOrderStatuses',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('access_token');
      const orderStatuses: OrderStatuses = { completed: 0, pending: 0, ready: 0 };

      const statuses = ['pending', 'completed', 'ready'];
      for (const status of statuses) {
        const response = await API.get(`/orders?type=${status}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.success) {
          orderStatuses[status as keyof OrderStatuses] = response.data.data.length;
        }
      }

      const totalOrders = Object.values(orderStatuses).reduce((a, b) => a + b, 0);
      return { orderStatuses, totalOrders };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch order statuses');
    }
  }
);

export const fetchSalesData = createAsyncThunk(
  'merchantDashboard/fetchSalesData',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('access_token');
      const today = new Date();
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

      const params = {
        startDate: startOfMonth.toISOString().split('T')[0],
        endDate: endOfMonth.toISOString().split('T')[0],
      };

      const response = await API.get('/analytics/sales', {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      if (response.data && response.data.success) {
        const salesData = response.data.data;
        const netSales = parseFloat(salesData.netSales) || 0;
        const grossSales = parseFloat(salesData.grossSales) || 0;
        return netSales || grossSales;
      }
      return 0;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch sales data');
    }
  }
);

export const fetchEarningsData = createAsyncThunk(
  'merchantDashboard/fetchEarningsData',
  async ({ merchantId, storeId }: { merchantId: string; storeId: string }, { rejectWithValue }) => {
    try {
      const today = moment();
      const yesterday = moment().subtract(1, 'day');

      const earnings = await EarningsAPI.getMerchantEarnings(merchantId, {
        startDate: yesterday,
        endDate: today,
        store: storeId,
      });

      return earnings.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch earnings data');
    }
  }
);

const merchantDashboardSlice = createSlice({
  name: 'merchantDashboard',
  initialState,
  reducers: {
    setSelectedCard: (state, action: PayloadAction<string | null>) => {
      // This can be handled in component state if needed
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch merchant data
      .addCase(fetchMerchantData.pending, (state) => {
        state.loading.merchant = true;
        state.error = null;
      })
      .addCase(fetchMerchantData.fulfilled, (state, action) => {
        state.loading.merchant = false;
        state.merchantData = action.payload.data;
        state.lastUpdated = action.payload.lastUpdated;
      })
      .addCase(fetchMerchantData.rejected, (state, action) => {
        state.loading.merchant = false;
        state.error = action.payload as string;
      })
      // Fetch stores
      .addCase(fetchStores.fulfilled, (state, action) => {
        state.storeId = action.payload;
      })
      // Fetch order statuses
      .addCase(fetchOrderStatuses.pending, (state) => {
        state.loading.orders = true;
      })
      .addCase(fetchOrderStatuses.fulfilled, (state, action) => {
        state.loading.orders = false;
        state.orderStatuses = action.payload.orderStatuses;
        state.orders = action.payload.totalOrders;
      })
      .addCase(fetchOrderStatuses.rejected, (state, action) => {
        state.loading.orders = false;
        state.error = action.payload as string;
      })
      // Fetch sales data
      .addCase(fetchSalesData.pending, (state) => {
        state.loading.sales = true;
      })
      .addCase(fetchSalesData.fulfilled, (state, action) => {
        state.loading.sales = false;
        state.sales = action.payload;
      })
      .addCase(fetchSalesData.rejected, (state, action) => {
        state.loading.sales = false;
        state.error = action.payload as string;
      })
      // Fetch earnings data
      .addCase(fetchEarningsData.pending, (state) => {
        state.loading.earnings = true;
      })
      .addCase(fetchEarningsData.fulfilled, (state, action) => {
        state.loading.earnings = false;
        state.earningsData = action.payload;
      })
      .addCase(fetchEarningsData.rejected, (state, action) => {
        state.loading.earnings = false;
        state.error = action.payload as string;
      });
  },
});

export const { setSelectedCard, clearError } = merchantDashboardSlice.actions;
export default merchantDashboardSlice.reducer;